/**
 * Hooks React Query pour gérer les activités (Learning Service)
 * Les appels API sont délégués au service learning.service.ts
 */

import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseInfiniteQueryOptions,
  type InfiniteData,
  UseQueryResult,
} from '@tanstack/react-query';
import { formatApiError } from '@sphere/shared/util';
import * as learningService from '@sphere/learning/data-access';
import type {
  UpdateActivityDto,
  ListActivitiesDto,
  SetStateDto,
  Activity,
  RemediationActivitiesResponse,
} from '@sphere/learning/domain';
import { IOffsetPaginationResponse } from '@sphere/shared/domain';
// ============================================================================
// Query Keys
// ============================================================================

export const activityKeys = {
  all: ['activities'] as const,
  lists: () => [...activityKeys.all, 'list'] as const,
  list: (filters: ListActivitiesDto) =>
    [...activityKeys.lists(), filters] as const,
  details: () => [...activityKeys.all, 'detail'] as const,
  detail: (id: string) => [...activityKeys.details(), id] as const,
  lessons: (id: string) => [...activityKeys.detail(id), 'lessons'] as const,
  classAssignments: (id: string) =>
    [...activityKeys.detail(id), 'class-assignments'] as const,
  userAssignments: (id: string) =>
    [...activityKeys.detail(id), 'user-assignments'] as const,
  published: () => [...activityKeys.all, 'published'] as const,
  remediation: (params?: Record<string, unknown>) =>
    [...activityKeys.all, 'remediation', params] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================
export function useActivities(
  params: ListActivitiesDto,
  options?: Omit<
    UseQueryOptions<IOffsetPaginationResponse<Activity>>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: activityKeys.list(params),
    queryFn: () => learningService.fetchActivities(params),
    ...options,
  });
}

export function useRemediationActivities(
  params?: {
    since_days?: number
    subject_id?: string
    weak_limit?: number
    activity_limit?: number
  },
  options?: Omit<
    UseQueryOptions<RemediationActivitiesResponse>,
    'queryKey' | 'queryFn'
  >
) {
  const normalized = params
    ? (Object.fromEntries(
        Object.entries(params).filter(
          ([_, v]) => v !== undefined && v !== ''
        )
      ) as typeof params)
    : undefined

  return useQuery({
    queryKey: activityKeys.remediation(normalized),
    queryFn: () => learningService.fetchRemediationActivities(normalized),
    staleTime: 1000 * 60 * 5,
    ...options,
  })
}

export function useExercises(
  params: ListActivitiesDto,
  options?: Omit<
    UseQueryOptions<IOffsetPaginationResponse<Activity>>,
    'queryKey' | 'queryFn'
  >
) {
  // Normaliser les paramètres pour la clé de cache (supprimer les valeurs undefined)
  const normalizedParams = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value !== undefined)
  ) as ListActivitiesDto;

  return useQuery({
    queryKey: [...activityKeys.lists(), 'exercises', normalizedParams],
    queryFn: () => learningService.fetchExercises(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: (previousData) => previousData, // Garde les données précédentes pendant le chargement
    ...options,
  });
}

/** Filtres exercices sans `page` — la page est gérée par le scroll infini */
export type InfiniteExercisesFilters = Omit<ListActivitiesDto, 'page'>;

/**
 * Liste d'exercices en scroll infini (pages concaténées côté client).
 * Clé `…/exercises/infinite/…` — les invalidations sur `…/exercises` s'appliquent aussi.
 */
export function useInfiniteExercises(
  baseFilters: InfiniteExercisesFilters,
  options?: Omit<
    UseInfiniteQueryOptions<
      IOffsetPaginationResponse<Activity>,
      Error,
      InfiniteData<IOffsetPaginationResponse<Activity>>,
      readonly unknown[],
      number
    >,
    'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
  >
) {
  const withDefaults: ListActivitiesDto = {
    ...baseFilters,
    limit: baseFilters.limit ?? 12,
  };
  const normalized = Object.fromEntries(
    Object.entries(withDefaults).filter(
      ([_, value]) => value !== undefined
    )
  ) as ListActivitiesDto;

  return useInfiniteQuery<
    IOffsetPaginationResponse<Activity>,
    Error,
    InfiniteData<IOffsetPaginationResponse<Activity>>,
    readonly unknown[],
    number
  >({
    queryKey: [...activityKeys.lists(), 'exercises', 'infinite', normalized],
    queryFn: ({ pageParam }) =>
      learningService.fetchExercises({
        ...normalized,
        page: pageParam as number,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pageInfo.hasNextPage
        ? (lastPage.pageInfo.page ?? 1) + 1
        : undefined,
    staleTime: 1000 * 60 * 5,
    ...options,
  });
}

export function useRevisions(
  params: ListActivitiesDto,
  options?: Omit<
    UseQueryOptions<IOffsetPaginationResponse<Activity>>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: [...activityKeys.lists(), 'revisions', params],
    queryFn: () => learningService.fetchRevisions(params),
    ...options,
  });
}

export function useActivity(
  id: string | undefined
): UseQueryResult<Activity, Error> {
  return useQuery({
    queryKey: activityKeys.detail(id!),
    queryFn: () => learningService.fetchActivityById(id!),
    enabled: !!id,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useCreateActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: learningService.createActivity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
      // Invalider aussi le cache des exercices
      queryClient.invalidateQueries({ queryKey: [...activityKeys.lists(), 'exercises'] });
    },
    onError: (error: any) => {
      const errorMsg = formatApiError(error);
      throw new Error(errorMsg.message);
    },
  });
}

export function useUpdateActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateActivityDto }) =>
      learningService.updateActivity(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: activityKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
      // Invalider aussi le cache des exercices
      queryClient.invalidateQueries({ queryKey: [...activityKeys.lists(), 'exercises'] });
    },
    onError: (error: any) => {
      const errorMsg = formatApiError(error);
      throw new Error(errorMsg.message);
    },
  });
}

export function useDeleteActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: learningService.deleteActivity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
      // Invalider aussi le cache des exercices
      queryClient.invalidateQueries({ queryKey: [...activityKeys.lists(), 'exercises'] });
    },
    onError: (error: any) => {
      const errorMsg = formatApiError(error);
      throw new Error(errorMsg.message);
    },
  });
}

export function useCloneActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: learningService.cloneActivity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
      // Invalider aussi le cache des exercices
      queryClient.invalidateQueries({ queryKey: [...activityKeys.lists(), 'exercises'] });
    },
    onError: (error: any) => {
      const errorMsg = formatApiError(error);
      throw new Error(errorMsg.message);
    },
  });
}

export function useSetActivityState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SetStateDto }) =>
      learningService.setActivityState(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: activityKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
      // Invalider aussi le cache des exercices
      queryClient.invalidateQueries({ queryKey: [...activityKeys.lists(), 'exercises'] });
    },
    onError: (error: any) => {
      const errorMsg = formatApiError(error);
      throw new Error(errorMsg.message);
    },
  });
}

export function usePublishActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => learningService.publishActivity(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: activityKeys.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
      // Invalider aussi le cache des exercices
      queryClient.invalidateQueries({ queryKey: [...activityKeys.lists(), 'exercises'] });
    },
    onError: (error: any) => {
      const errorMsg = formatApiError(error);
      throw new Error(errorMsg.message);
    },
  });
}

export function useSetActivityVisibility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, visibility }: { id: string; visibility: string }) =>
      learningService.setActivityVisibility(id, visibility),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: activityKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
      // Invalider aussi le cache des exercices
      queryClient.invalidateQueries({ queryKey: [...activityKeys.lists(), 'exercises'] });
    },
    onError: (error: any) => {
      const errorMsg = formatApiError(error);
      throw new Error(errorMsg.message);
    },
  });
}

export function useAssignToClass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: learningService.createActivityTeachingAssignment,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: activityKeys.classAssignments(variables.activityId),
      });
    },
    onError: (error: any) => {
      const errorMsg = formatApiError(error);
      throw new Error(errorMsg.message);
    },
  });
}

export function useAssignToUsers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: learningService.createActivityUserAssignment,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: activityKeys.userAssignments(variables.activityId),
      });
    },
    onError: (error: any) => {
      const errorMsg = formatApiError(error);
      throw new Error(errorMsg.message);
    },
  });
}

export function useActivityTypes() {
  return useQuery({
    queryKey: ['activity-types'],
    queryFn: () => learningService.fetchActivityTypes(),
  });
}

export function useActivityType(type: string) {
  return useQuery({
    queryKey: ['activity-type', type],
    queryFn: () => learningService.fetchActivityType(type),
  });
}

export function useActivityStates() {
  return useQuery({
    queryKey: ['activity-states'],
    queryFn: () => learningService.fetchActivityStates(),
  });
}

export function usePreviewGenerateActivity() {
  return useMutation({
    mutationFn: learningService.previewGenerateActivity,
    onError: (error: any) => {
      const errorMsg = formatApiError(error);
      throw new Error(errorMsg.message);
    },
  });
}

export function useApproveActivityFromAI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: learningService.approveActivityFromAI,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
      // Invalider aussi le cache des exercices
      queryClient.invalidateQueries({ queryKey: [...activityKeys.lists(), 'exercises'] });
    },
    onError: (error: any) => {
      const errorMsg = formatApiError(error);
      throw new Error(errorMsg.message);
    },
  });
}
